// frontend/src/components/Events/EditEventModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  FormHelperText,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { eventsAPI } from '../../services/eventsAPI';
import { UNIVERSITY_BRANCHES } from '../../data/scienceData';

// Константы для типов мероприятий
const EVENT_TYPES = [
  { value: 'conference', label: 'Конференция', icon: '🗣️', color: '#4361ee', hasSections: true },
  { value: 'seminar', label: 'Семинар', icon: '📚', color: '#3a0ca3', hasSections: false },
  { value: 'symposium', label: 'Симпозиум', icon: '🔬', color: '#7209b7', hasSections: true },
  { value: 'workshop', label: 'Воркшоп', icon: '🛠️', color: '#f72585', hasSections: false },
  { value: 'school', label: 'Школа', icon: '🏫', color: '#4cc9f0', hasSections: true },
  { value: 'congress', label: 'Конгресс', icon: '🌍', color: '#4895ef', hasSections: true },
  { value: 'forum', label: 'Форум', icon: '🗣️', color: '#560bad', hasSections: true },
  { value: 'roundtable', label: 'Круглый стол', icon: '🔄', color: '#b5179e', hasSections: false },
  { value: 'competition', label: 'Конкурс', icon: '🏆', color: '#f8961e', hasSections: false },
  { value: 'festival', label: 'Фестиваль', icon: '🎪', color: '#f94144', hasSections: true }
];

// Константы для уровней мероприятий
const EVENT_LEVELS = [
  { value: 'international', label: 'Международный', color: '#9C27B0' },
  { value: 'national', label: 'Всероссийский', color: '#2196F3' },
  { value: 'interregional', label: 'Межрегиональный', color: '#4CAF50' },
  { value: 'regional', label: 'Региональный', color: '#FF9800' },
  { value: 'university', label: 'Внутривузовский', color: '#795548' },
  { value: 'interuniversity', label: 'Межвузовский', color: '#FF6B6B' }
];

const STEPS = ['Основная информация', 'Даты и место', 'Секции', 'Документы'];

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': { padding: theme.spacing(2) },
  '& .MuiDialog-paper': {
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 760,
    minWidth: 0,
    height: 'min(82vh, 760px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.palette.grey[100], 0.50)} 58%, ${alpha(theme.palette.primary.main, 0.055)}), radial-gradient(circle at 92% 8%, ${alpha(theme.palette.primary.main, 0.11)}, transparent 34%)`,
    backgroundColor: alpha(theme.palette.common.white, 0.62),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32)
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.2, 2.6),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.60)}, ${alpha(theme.palette.common.white, 0.18)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`
}));

const GlassBlock = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2.8),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.54)}, ${alpha(theme.palette.common.white, 0.20)})`,
  border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
  backdropFilter: 'blur(14px) saturate(130%)',
  WebkitBackdropFilter: 'blur(14px) saturate(130%)',
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.045)}`
}));

const GlassTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: alpha(theme.palette.common.white, 0.42),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.78) },
    '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.30) },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.common.white, 0.64),
      '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.52) }
    }
  }
}));

const GlassSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  backgroundColor: alpha(theme.palette.common.white, 0.42),
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.common.white, 0.78) },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.30) },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.52) }
}));

const StepperShell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.15, 1.2),
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.common.white, 0.40),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  '& .MuiStepLabel-label': { fontSize: '0.68rem', fontWeight: 650, color: theme.palette.text.secondary },
  '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': { color: theme.palette.primary.main },
  '& .MuiStepIcon-root': { fontSize: 24, color: alpha(theme.palette.text.secondary, 0.24) },
  '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: theme.palette.primary.main }
}));

const EditEventModal = ({ open, onClose, event, onSave }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Данные формы
  const [formData, setFormData] = useState({
    type: '',
    level: '',
    title: '',
    short_description: '',
    description: '',
    start_date: null,
    end_date: null,
    registration_deadline: null,
    organizer_branches: [],
    sections: [],
    documents: [],
    additional_info: ''
  });

  // Для секций
  const [sectionTitle, setSectionTitle] = useState('');
  const [sectionDescription, setSectionDescription] = useState('');

  // Для документов
  const [documentFiles, setDocumentFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Ошибки валидации
  const [errors, setErrors] = useState({});

  // Определяем, нужно ли показывать секции
  const selectedType = EVENT_TYPES.find(t => t.value === formData.type);
  const showSections = selectedType?.hasSections || false;

  // Заполняем форму данными мероприятия при открытии
  useEffect(() => {
    if (event && open) {
      setFormData({
        type: event.type || '',
        level: event.level || '',
        title: event.title || '',
        short_description: event.short_description || '',
        description: event.description || '',
        start_date: event.start_date ? new Date(event.start_date) : null,
        end_date: event.end_date ? new Date(event.end_date) : null,
        registration_deadline: event.registration_deadline ? new Date(event.registration_deadline) : null,
        organizer_branches: event.organizer_branches || [],
        sections: event.sections || [],
        documents: event.documents || [],
        additional_info: event.additional_info || ''
      });
      setDocumentFiles([]);
      setActiveStep(0);
      setErrors({});
    }
  }, [event, open]);

  // Валидация шага
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.type) newErrors.type = 'Выберите тип мероприятия';
      if (!formData.level) newErrors.level = 'Выберите уровень мероприятия';
      if (!formData.title.trim()) newErrors.title = 'Введите название мероприятия';
      if (!formData.short_description.trim()) newErrors.short_description = 'Введите краткое описание';
    }

    if (step === 1) {
      if (!formData.start_date) newErrors.start_date = 'Укажите дату начала';
      if (!formData.end_date) newErrors.end_date = 'Укажите дату окончания';
      if (!formData.registration_deadline) newErrors.registration_deadline = 'Укажите дедлайн регистрации';
      if (formData.organizer_branches.length === 0) newErrors.organizer_branches = 'Укажите филиалы-организаторы';

      if (formData.start_date && formData.end_date) {
        if (new Date(formData.start_date) > new Date(formData.end_date)) {
          newErrors.end_date = 'Дата окончания не может быть раньше даты начала';
        }
      }

      if (formData.registration_deadline && formData.start_date) {
        if (new Date(formData.registration_deadline) > new Date(formData.start_date)) {
          newErrors.registration_deadline = 'Дедлайн регистрации должен быть раньше даты начала';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddSection = () => {
    if (sectionTitle.trim()) {
      setFormData(prev => ({
        ...prev,
        sections: [
          ...prev.sections,
          {
            id: Date.now(),
            title: sectionTitle,
            description: sectionDescription
          }
        ]
      }));
      setSectionTitle('');
      setSectionDescription('');
    }
  };

  const handleRemoveSection = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);

    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      file: file,
      url: URL.createObjectURL(file)
    }));

    setDocumentFiles(prev => [...prev, ...newFiles]);
    setUploading(false);
  };

  const handleRemoveFile = (fileId) => {
    setDocumentFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // 🔥 ОБНОВЛЕННЫЙ МЕТОД ОТПРАВКИ

const handleSubmit = async () => {
  setLoading(true);
  setError('');
  setSuccess('');

  try {
    // Финальная валидация
    if (!formData.type || !formData.level || !formData.title.trim() || !formData.short_description.trim()) {
      setError('Пожалуйста, заполните все обязательные поля');
      setLoading(false);
      return;
    }

    // 🔥 ИСПРАВЛЕНО: подготавливаем секции со всеми существующими полями
    const sectionsToSend = formData.sections.map(section => ({
      id: section.id,  // ID существующей секции (важно!)
      title: section.title,
      description: section.description || '',
      // 🔥 ДОБАВЛЯЕМ ВСЕ СУЩЕСТВУЮЩИЕ ПОЛЯ
      about: section.about || '',
      cover_images: section.cover_images || [],
      color: section.color || '#4CAF50',
      // можно добавить и другие поля, если они есть в модели
    }));

    const eventData = {
      ...formData,
      start_date: formData.start_date?.toISOString().split('T')[0],
      end_date: formData.end_date?.toISOString().split('T')[0],
      registration_deadline: formData.registration_deadline?.toISOString().split('T')[0],
      documents: formData.documents,
      sections_data: sectionsToSend,
      has_sections: formData.sections.length > 0
    };

    const response = await eventsAPI.updateEvent(event.id, eventData);
    setSuccess('Изменения успешно сохранены!');

    if (onSave) {
      onSave(response);
    }

    setTimeout(() => {
      onClose();
    }, 1500);

  } catch (err) {
    console.error('❌ Ошибка сохранения:', err);
    setError('Не удалось сохранить изменения');
  } finally {
    setLoading(false);
  }
};

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.10),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              color: 'primary.main'
            }}>
              <EventIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight="700" noWrap>
                Редактирование мероприятия
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Основные параметры, даты, секции и документы
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              width: 40, height: 40, flexShrink: 0,
              color: 'text.secondary',
              backgroundColor: alpha(theme.palette.common.white, 0.44),
              border: `1px solid ${alpha(theme.palette.common.white, 0.80)}`,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.09)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <DialogContent
          sx={{
            p: 2.2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.text.secondary, 0.16),
              borderRadius: 999
            }
          }}
        >
          {/* Степпер */}
          <StepperShell sx={{ mt: 2, mb: 1.8 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </StepperShell>

          {/* Сообщения */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {/* ШАГ 1: Основная информация */}
          {activeStep === 0 && (
            <GlassBlock sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2.2 }}>
              <Box>
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Тип мероприятия *</InputLabel>
                  <GlassSelect
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    label="Тип мероприятия *"
                    sx={{ borderRadius: 2 }}
                  >
                    {EVENT_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{type.icon}</span>
                          <span>{type.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </GlassSelect>
                  {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                </FormControl>
              </Box>

              <Box>
                <FormControl fullWidth error={!!errors.level}>
                  <InputLabel>Уровень мероприятия *</InputLabel>
                  <GlassSelect
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    label="Уровень мероприятия *"
                    sx={{ borderRadius: 2 }}
                  >
                    {EVENT_LEVELS.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </GlassSelect>
                  {errors.level && <FormHelperText>{errors.level}</FormHelperText>}
                </FormControl>
              </Box>

              <Box>
                <GlassTextField
                  fullWidth
                  label="Название мероприятия *"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  error={!!errors.title}
                  helperText={errors.title}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>

              <Box>
                <GlassTextField
                  fullWidth
                  label="Краткое описание *"
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleInputChange}
                  error={!!errors.short_description}
                  helperText={errors.short_description || 'Максимум 300 символов'}
                  inputProps={{ maxLength: 300 }}
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>

              <Box>
                <GlassTextField
                  fullWidth
                  label="Полное описание"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={5}
                  helperText="Подробная информация о мероприятии"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </GlassBlock>
          )}

          {/* ШАГ 2: Даты и место */}
          {activeStep === 1 && (
            <GlassBlock sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2.2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Дата начала *"
                    value={formData.start_date}
                    onChange={(date) => handleDateChange('start_date', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.start_date,
                        helperText: errors.start_date,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Дата окончания *"
                    value={formData.end_date}
                    onChange={(date) => handleDateChange('end_date', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.end_date,
                        helperText: errors.end_date,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Дедлайн регистрации *"
                    value={formData.registration_deadline}
                    onChange={(date) => handleDateChange('registration_deadline', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.registration_deadline,
                        helperText: errors.registration_deadline,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={UNIVERSITY_BRANCHES}
                    value={formData.organizer_branches}
                    onChange={(e, newValue) => {
                      setFormData(prev => ({ ...prev, organizer_branches: newValue }));
                      if (errors.organizer_branches) {
                        setErrors(prev => ({ ...prev, organizer_branches: '' }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Филиалы-организаторы *"
                        error={!!errors.organizer_branches}
                        helperText={errors.organizer_branches}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          sx={{ borderRadius: 4 }}
                        />
                      ))
                    }
                  />
                </Grid>
              </Grid>

              <Box>
                <GlassTextField
                  fullWidth
                  label="Дополнительная информация"
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  helperText="История, цели, дополнительная информация о мероприятии"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </GlassBlock>
          )}

          {/* ШАГ 3: Секции */}
          {activeStep === 2 && (
            <GlassBlock sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2.2 }}>
              {!showSections ? (
                <Alert severity="info" icon={<EventIcon />} sx={{ borderRadius: 2 }}>
                  Для выбранного типа мероприятия секции не предусмотрены
                </Alert>
              ) : (
                <>
                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2.8, bgcolor: alpha(theme.palette.common.white, 0.30), borderColor: alpha(theme.palette.common.white, 0.72), boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <GlassTextField
                        fullWidth
                        size="medium"
                        label="Название секции"
                        value={sectionTitle}
                        onChange={(e) => setSectionTitle(e.target.value)}
                        placeholder="Например: Секция экономики"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />

                      <GlassTextField
                        fullWidth
                        size="medium"
                        label="Описание (опционально)"
                        value={sectionDescription}
                        onChange={(e) => setSectionDescription(e.target.value)}
                        placeholder="Кратко о тематике секции"
                        multiline
                        rows={2}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />

                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddSection}
                        disabled={!sectionTitle.trim()}
                        sx={{
                          borderRadius: 999,
                          height: 46,
                          boxShadow: 'none',
                          '&:hover': { boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.18)}` }
                        }}
                      >
                        Добавить секцию
                      </Button>
                    </Box>
                  </Card>

                  {formData.sections.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="subtitle1" fontWeight="600" color="primary">
                        Текущие секции:
                      </Typography>
                      {formData.sections.map((section, index) => {
                        const colors = ['#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0'];
                        const color = colors[index % colors.length];

                        return (
                          <Card key={section.id} variant="outlined" sx={{
                              borderRadius: 2.8,
                              backgroundColor: alpha(theme.palette.common.white, 0.34),
                              borderColor: alpha(theme.palette.common.white, 0.76),
                              boxShadow: 'none'
                            }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: alpha(color, 0.14), color, width: 40, height: 40, fontWeight: 700 }}>
                                    {index + 1}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="600">
                                      {section.title}
                                    </Typography>
                                    {section.description && (
                                      <Typography variant="body2" color="text.secondary">
                                        {section.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveSection(section.id)}
                                  sx={{ borderRadius: 4 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </GlassBlock>
          )}

          {/* ШАГ 4: Документы */}
{activeStep === 3 && (
  <GlassBlock sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2.2 }}>
    <Card variant="outlined" sx={{ p: 3, borderRadius: 2.8, bgcolor: alpha(theme.palette.common.white, 0.30), borderColor: alpha(theme.palette.common.white, 0.72), boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', textAlign: 'center' }}>
        <Box sx={{
          width: 58, height: 58, borderRadius: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.10),
          color: 'primary.main'
        }}>
          <FolderIcon sx={{ fontSize: 30 }} />
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>
            Загрузите дополнительные документы
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Положение, программа, требования и другие материалы
          </Typography>
        </Box>

        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
          disabled={uploading}
          sx={{ borderRadius: 999, px: 3.5, py: 1.2, boxShadow: 'none' }}
        >
          {uploading ? 'Загрузка...' : 'Выбрать файлы'}
          <input
            type="file"
            hidden
            multiple
            onChange={handleFileUpload}
          />
        </Button>
      </Box>
    </Card>

    {formData.documents && formData.documents.length > 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" fontWeight="600" color="primary">
          Существующие документы:
        </Typography>
        <List sx={{
          bgcolor: alpha(theme.palette.common.white, 0.34),
          borderRadius: 2.8,
          border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`
        }}>
          {formData.documents
            .filter(doc => doc !== null && doc !== undefined)
            .map((doc, index) => (
              <ListItem key={doc?.id || index}>
                <ListItemIcon>
                  <DescriptionIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={doc?.name || 'Документ'}
                  secondary={doc?.size ? formatFileSize(doc.size) : ''}
                />
              </ListItem>
            ))}
        </List>
      </Box>
    )}

    {documentFiles.length > 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" fontWeight="600" color="primary">
          Новые документы:
        </Typography>
        <List sx={{
          bgcolor: alpha(theme.palette.common.white, 0.34),
          borderRadius: 2.8,
          border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`
        }}>
          {documentFiles
            .filter(file => file !== null && file !== undefined)
            .map((file) => (
              <ListItem
                key={file.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveFile(file.id)}
                    sx={{ borderRadius: 4 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <DescriptionIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name || 'Документ'}
                  secondary={formatFileSize(file.size)}
                />
              </ListItem>
            ))}
        </List>
      </Box>
    )}
  </GlassBlock>
)}
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.6,
            px: 2.2,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.58)}, ${alpha(theme.palette.common.white, 0.22)})`,
            borderTop: `1px solid ${alpha(theme.palette.common.white, 0.72)}`
          }}
        >
          <Button
            onClick={activeStep === 0 ? onClose : handleBack}
            variant="outlined"
            startIcon={activeStep === 0 ? <CloseIcon /> : <ArrowBackIcon />}
            sx={{
              borderRadius: 999,
              px: 2.6,
              borderColor: alpha(theme.palette.common.white, 0.82),
              backgroundColor: alpha(theme.palette.common.white, 0.34),
              color: 'text.primary'
            }}
          >
            {activeStep === 0 ? 'Отмена' : 'Назад'}
          </Button>

          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                borderRadius: 999,
                px: 3.2,
                boxShadow: 'none',
                '&:hover': { boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.18)}` }
              }}
            >
              Далее
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{
                borderRadius: 999,
                px: 3.2,
                boxShadow: 'none',
                '&:hover': { boxShadow: `0 8px 22px ${alpha(theme.palette.success.main, 0.18)}` }
              }}
            >
              {loading ? 'Сохранение...' : 'Сохранить изменения'}
            </Button>
          )}
        </DialogActions>
      </StyledDialog>
    </LocalizationProvider>
  );
};

export default EditEventModal;